import { Box, Typography } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import './StatsTile.css';

interface StatsTileProps {
  icon: SvgIconComponent;
  value: string | number | null;
  unit?: string;
  label?: string;
  isCompact?: boolean;
  isActive?: boolean;
  isLandscape?: boolean; // New prop for landscape mode
}

export const StatsTile = ({ 
  icon: Icon, 
  value, 
  unit = '', 
  label,
  isCompact = false, 
  isActive = false,
  isLandscape = false
}: StatsTileProps) => {
  const formattedValue = value === null ? '-' : `${value}${unit ? ' ' + unit : ''}`;
  
  const tileClasses = [
    'stats-tile',
    isCompact && 'stats-tile--compact'
  ].filter(Boolean).join(' ');
  
  const contentClasses = [
    'stats-tile__content',
    isCompact && 'stats-tile__content--compact',
    isActive && 'stats-tile__content--active'
  ].filter(Boolean).join(' ');
  
  const iconClasses = [
    'stats-tile__icon',
    isActive && 'stats-tile__icon--active'
  ].filter(Boolean).join(' ');
  
  const textClasses = [
    'stats-tile__text',
    isActive && 'stats-tile__text--active'
  ].filter(Boolean).join(' ');
  
  return (
    <Box className={tileClasses} sx={{
      // Override fixed heights in landscape mode
      ...(isLandscape && {
        height: '48px !important',
        minHeight: '48px !important',
        maxHeight: '48px !important'
      })
    }}>
      <Box className={contentClasses} sx={{
        // Override content heights and spacing in landscape
        ...(isLandscape && {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
          padding: '4px 8px !important',
          gap: '6px !important',
          flexDirection: 'row !important', // Horizontal layout in landscape
          justifyContent: 'center !important',
          alignItems: 'center !important'
        })
      }}>
        <Icon className={iconClasses} sx={{
          // Smaller icon in landscape
          fontSize: isLandscape ? '1.1rem !important' : undefined
        }} />
        {!isCompact && (
          <Box sx={{
            display: 'flex',
            flexDirection: isLandscape ? 'row' : 'column',
            alignItems: 'center',
            gap: isLandscape ? '4px' : '2px'
          }}>
            {label && (
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: isLandscape ? '0.55rem' : '0.6rem', 
                  color: isActive ? 'white' : 'text.secondary', 
                  fontWeight: 500,
                  textAlign: 'center',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  marginTop: isLandscape ? '0px' : '4px',
                  marginBottom: isLandscape ? '0px' : '2px'
                }}
              >
                {label}:
              </Typography>
            )}
            <Typography 
              variant="body1" 
              fontWeight={700}
              noWrap 
              className={textClasses}
              sx={{
                fontSize: isLandscape ? '0.75rem' : '0.9rem',
                fontWeight: 700,
                marginTop: isLandscape ? '0px' : '2px'
              }}
            >
              {formattedValue}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
